import type { ProjectsResponse } from '../../api/projects-client/projects-client.type';
import type { WorkPeriod } from '../../api/work-periods-client/work-periods-client.type';
import { formatToISODate, safeParseDateInput } from '../../helpers/date-time-helpers';
import type { FloraContextObject, FloraContextualInfo } from '../flora/flora.type';
import { FloraSupportedObjectTypes } from '../flora/flora.type';
import { GeneralViewNames } from '../side-bar/side-bar.type';
import { DateRange, WorkPeriodType } from './work-periods.type';

/**
 * Creates Flora contextual info from work period store state.
 *
 * The date range shown in the Flora context banner must match the active
 * work-period mode:
 * - `WorkPeriodType.Custom` (ad hoc) — use the user-picked `dateRange`.
 * - `WorkPeriodType.Defined` (sprint) — derive from `selectedWorkPeriods`,
 *   ignoring any stale `dateRange` that may linger from a previous ad hoc
 *   session (the store persists `dateRange` to localStorage).
 *
 * @param {string | null} selectedBoardId - The currently selected board ID
 * @param {WorkPeriod[]} selectedWorkPeriods - Array of selected work periods
 * @param {DateRange} dateRange - The selected date range (ad hoc only)
 * @param {ProjectsResponse[]} teams - Array of teams/projects
 * @param {WorkPeriodType} selectedWorkPeriodType - The active work period mode
 * @param {string[]} selectedMeasures - Array of selected measure IDs
 * @returns {FloraContextualInfo} Flora contextual info with current objects and date range
 */
function getWorkPeriodsFloraContext(
  selectedBoardId: string | null,
  selectedWorkPeriods: WorkPeriod[],
  dateRange: DateRange,
  teams: ProjectsResponse[],
  selectedWorkPeriodType: WorkPeriodType,
  selectedMeasures: string[] = [],
): FloraContextualInfo {
  const objects: FloraContextObject[] = [];

  // Add team/project and board if selected
  if (selectedBoardId) {
    // Find the team that owns this board
    for (const team of teams) {
      const board = team.subprojects?.find((subproject) => subproject.id === selectedBoardId);
      if (board) {
        // Add the board as subproject
        objects.push({
          type: FloraSupportedObjectTypes.Subproject,
          id: board.id,
          name: board.name,
        });
        break;
      }
    }
  }

  // Add all selected work periods
  for (const workPeriod of selectedWorkPeriods) {
    objects.push({
      type: FloraSupportedObjectTypes.WorkPeriod,
      id: workPeriod.id,
      name: workPeriod.name,
    });
  }

  // Determine the date range based on the active mode. In sprint (Defined)
  // mode the selected work periods are authoritative, even if a stale ad-hoc
  // `dateRange` is still in the store from a previous Custom session.
  let startDate: string | null = null;
  let endDate: string | null = null;

  if (selectedWorkPeriodType === WorkPeriodType.Custom) {
    startDate = formatToISODate(dateRange.startDate);
    endDate = formatToISODate(dateRange.endDate);
  } else if (selectedWorkPeriods.length > 0) {
    const parsedStartDates = selectedWorkPeriods
      .map((wp) => safeParseDateInput(wp.start_date))
      .filter((date) => date !== null);
    const parsedEndDates = selectedWorkPeriods
      .map((wp) => safeParseDateInput(wp.end_date))
      .filter((date) => date !== null);

    if (parsedStartDates.length > 0 && parsedEndDates.length > 0) {
      const minStartDate = parsedStartDates.reduce((min, current) =>
        current && (!min || current.isBefore(min)) ? current : min,
      );
      const maxEndDate = parsedEndDates.reduce((max, current) =>
        current && (!max || current.isAfter(max)) ? current : max,
      );

      startDate = formatToISODate(minStartDate);
      endDate = formatToISODate(maxEndDate);
    }
  }

  return {
    currentModule: GeneralViewNames.WorkPeriods,
    currentObjects: objects,
    startDate,
    endDate,
    selectedMeasures,
  };
}

export { getWorkPeriodsFloraContext };
